"use client";

import { getApps, getApp, initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import type { AppCheck } from "firebase/app-check";

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY!,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN!,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID!,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET!,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID!,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

// ---- Singleton app ----
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

// ---- App Check init (client-only, once) ----
declare global {
  var __APP_CHECK_INIT__: boolean | undefined;
  var __APP_CHECK_INSTANCE__: AppCheck | undefined;
}

let appCheckInstance: AppCheck | undefined =
  typeof window !== "undefined" ? globalThis.__APP_CHECK_INSTANCE__ : undefined;

function initAppCheck() {
  if (globalThis.__APP_CHECK_INIT__) return;

  const siteKey = process.env.NEXT_PUBLIC_FIREBASE_APPCHECK_SITE_KEY?.trim();

  if (!siteKey) {
    console.warn("[AppCheck] Missing NEXT_PUBLIC_FIREBASE_APPCHECK_SITE_KEY");
    globalThis.__APP_CHECK_INIT__ = true;
    return;
  }

  // A key prefix cannot identify the provider: reCAPTCHA v3 and Enterprise
  // site keys can both begin with "6L". Production uses the score-based
  // Enterprise key registered for this Firebase web app.
  const provider = new ReCaptchaEnterpriseProvider(siteKey);

  const isLocalDebugHost =
    location.hostname === "localhost" || location.hostname === "127.0.0.1";
  if (process.env.NODE_ENV !== "production" && isLocalDebugHost) {
    const debugFlagName = ["FIREBASE", "APPCHECK", "DEBUG", "TOKEN"].join("_");
    (self as unknown as Record<string, boolean>)[debugFlagName] = true;
  }

  const instance = initializeAppCheck(app, {
    provider,
    isTokenAutoRefreshEnabled: true,
  });

  appCheckInstance = instance;
  globalThis.__APP_CHECK_INSTANCE__ = instance;
  globalThis.__APP_CHECK_INIT__ = true;

  console.log("[AppCheck] Initialized (reCAPTCHA Enterprise)", {
    host: location.host,
    appId: app.options.appId,
  });
}

// Defer App Check init until the DOM is ready so reCAPTCHA can
// find / create its invisible placeholder element.
if (typeof window !== "undefined" && !globalThis.__APP_CHECK_INIT__) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAppCheck, { once: true });
  } else {
    // DOM already parsed (interactive or complete) — safe to init now
    initAppCheck();
  }
}

export function getAppCheckInstance(): AppCheck {
  if (typeof window === "undefined") {
    throw new Error("App Check is only available in the browser");
  }

  // If init was deferred and hasn't run yet, run it now
  if (!globalThis.__APP_CHECK_INIT__) {
    initAppCheck();
  }

  const instance = appCheckInstance ?? globalThis.__APP_CHECK_INSTANCE__;
  if (!instance) {
    throw new Error("App Check has not been initialized");
  }

  return instance;
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export default app;
